<template>
  <div :class="`job-list ${parentClass} ${carousel ? 'job-list--carousel' : ''}`">
    <div class="job-list__header d-flex justify-space-between mb-6">
      <div>
        <AppTypography type="headline-1" class="mb-1">{{ title }}</AppTypography>
        <AppTypography type="subheadline-2">{{ subtitle }}</AppTypography>
      </div>

      <AppButton variant="text" size="small" color="primary" class="job-list__view" @click="toggleViewAll">
        View all
        <v-icon icon="mdi-chevron-right" class="ml-1" />
      </AppButton>
    </div>        
    <div class="job-list__list">
      <template v-if="!fetching">
        <template v-if="jobListings.length > 0">
          <template v-if="!carousel">
            <v-row no-gutter>
              <v-col v-for="(job, index) in jobListings" :key="index" :index="index" cols="12" md="4">{{ job }}
                <JobListingCard
                  :id="job.id"
                  :title="job.title"
                  :description="job.description"
                  :maxPayrate="job.max_payrate"
                  :minPayrate="job.min_payrate"
                  :isFullyVerified="isFullyVerified"
                  class="job-list__list__item"
                  @view="viewJob(job.id)"
                />
              </v-col>
            </v-row>
          </template>
          <template v-else>
            <div class="job-list__scroll-row">
              <div
                v-for="(job, index) in jobListings.slice(0, 10)"
                :key="index"
                class="job-list__scroll-card"
              >
              <!-- :price="job.pay_rate.hourly" -->
                <JobListingCard
                  :id="job.id"
                  :title="job.title"
                  :description="job.description"
                  :address="job.address"
                  :maxPayrate="job.max_payrate"
                  :minPayrate="job.min_payrate"
                  :days_time="job.days_time"
                  :isFullyVerified="isFullyVerified"
                  class="job-list__list__item"
                  @view="viewJob(job.id)"
                />
              </div>
            </div>
          </template>
        </template>
        <template v-else>
          <div class="no-data-container text-center py-12">
            <v-icon size="80" color="grey-lighten-1" class="mb-4">mdi-briefcase-search-outline</v-icon>
            <h3 class="text-h5 font-weight-medium mb-3 text-high-emphasis">Add more Qualifications and expand your Travel</h3>
            <p class="text-body-1 text-medium-emphasis mb-6" style="max-width: 400px; margin: 0 auto;">
              Try adding your qualifications or updating your travel preferences to see more opportunities. New listings are posted regularly, so check back soon.
            </p>
            <AppButton 
              color="primary" 
              variant="outlined"
              @click="fetch(1)"
              prepend-icon="mdi-refresh"
              class="text-none"
            >
              Refresh
            </AppButton>
          </div>
        </template>
      </template>
      <template v-else>
        <v-row no-gutter>
          <v-col v-for="n in 3" :key="n" :index="n" cols="12" md="4">
            <div class="job-list__list__loader">
              <v-skeleton-loader type="list-item-three-line" />
              <v-skeleton-loader type="heading" />
            </div>
          </v-col>
        </v-row>
      </template>
    </div>
  </div>

  <ComplateProfileDialog v-model="showVerification" />
  <JobListingApplicationForm v-model="showViewing" :job-listing="selectedJob" />
</template>
<script lang="ts" setup>
import icFilter from '@/assets/img/icon/ic-filter.png';
import { AppButton, AppTypography } from '@/components/base';
import { ComplateProfileDialog } from '@/components';
import { JobListingApplicationForm } from '@/components/support-seeker/forms';
import { useAuthStore } from '@/modules/auth/store/auth';
import { JobListingCard } from '@/components/support-seeker';
import { Carousel, Slide, Pagination, Navigation } from 'vue3-carousel';
import type JobListing from '@/types/JobListing';
import { useRouter } from 'vue-router';
import { onMounted, onBeforeUnmount } from 'vue';

const router = useRouter();

const props = withDefaults(
  defineProps<{
    title?: string;
    subtitle?: string;
    parentClass?: string;

    carousel?: boolean;
    limit?: number;

    recommended?: boolean;
  }>(),
  {
    title: 'Job listings',
    subtitle: 'Job listings were recently posted',

    carousel: false,
    limit: 10,

    recommended: false,
  }
);

// Define emits
const emit = defineEmits<{
  'jobListingsLength': [length: number]
}>();

const page = ref(1);
const showVerification = ref(false);
const showViewing = ref(false);
const selectedJob = ref<JobListing | null>(null);
const { isFullyVerified } = useAuthStore();
const { jobListings, jobListingsMeta, fetching, getJobListings } = useJobListing();

// Watch for changes in jobListings and emit the length
watch(jobListings, (newJobListings) => {
  if (newJobListings) {
    emit('jobListingsLength', newJobListings.length);
  }
}, { immediate: true });

const fetch = async (newPage: number = 1): Promise<void> => {
  page.value = newPage;
  await getJobListings(page.value, props.limit, props.recommended);
};

const viewJob = async (jobId: number): Promise<void> => {
  try {
    const { data } = await useFetchData<any>('/jobs/' + jobId, {
      method: 'GET',
    });
    if (data.value) {
      selectedJob.value = data.value;
    }
  } catch (error) {
    console.error('Error fetching job details:', error);
  }
  toggleViewing(!!selectedJob.value);
};

const toggleViewing = (bool: boolean): void => {
  showViewing.value = bool;
};

const toggleViewAll = (): void => {
  // console.log("props",props.title);
  if (props.recommended) {
    router.push('/recommended-jobs');
  } else {
    router.push('/recent-jobs');
  }
  // if (1==1) {
  //   toggleVerification(true);
  // }
};

const toggleVerification = (bool: boolean = null): void => {
  showVerification.value = bool === null ? !showVerification.value : bool;
};

// Add refresh method for external calls
const refreshData = async (): Promise<void> => {
  await fetch(1);
};

// Expose the refresh method
defineExpose({
  refreshData
});

function horizontalScroll(e) {
  // Only scroll horizontally if the user is not holding shift (which already scrolls horizontally)
  if (Math.abs(e.deltaY) > Math.abs(e.deltaX) && !e.shiftKey) {
    e.preventDefault();
    e.currentTarget.scrollLeft += e.deltaY;
  }
}

onMounted(() => {
  nextTick(() => {
    fetch();
    // if (isFullyVerified) {
    // }
  });

  const row = document.querySelector('.job-list__scroll-row');
  if (row) {
    row.addEventListener('wheel', horizontalScroll, { passive: false });
  }
});

onBeforeUnmount(() => {
  const row = document.querySelector('.job-list__scroll-row');
  if (row) {
    row.removeEventListener('wheel', horizontalScroll);
  }
});
</script>
<style lang="scss" scoped>
.job-list {
  &__view {
  }

  &__list {
    &__loader {
      border-radius: 16px;
      box-shadow: 0px 8px 9px 0px #0000001a;
      overflow: hidden;
    }
  }
}

.job-list--carousel {
  :deep(.carousel) {
    .carousel__viewport {
      padding: 0 5px;
    }

    .carousel__slide {
      max-width: 345px;
      margin-top: 10px;
      margin-bottom: 15px;

      &:not(:last-child) {
        margin-right: 20px;
      }
    }
  }

  .job-list__list {
    &__item {
    }
  }

  @media (max-width: 600px) {
    :deep(.carousel) {
      .carousel__viewport {
        overflow: visible;
      }

      .carousel__slide {
        max-width: 100%;
      }
    }
  }
}

.job-list__scroll-row {
  display: flex;
  overflow-x: auto;
  gap: 20px;
  padding: 0 5px 10px 5px;
  // scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar { display: none; }
  -ms-overflow-style: none;
  scrollbar-width: none;
  transform: translateZ(0);
}

.job-list__scroll-card {
  flex: 0 0 320px;
  max-width: 100%;
  scroll-snap-align: start;
}
</style>
